import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, Play, Share2, Pause, Flame, Clock, Target } from "lucide-react";
import { toast } from "sonner";

const PHASE_LABEL = { work: "focus", short_break: "short_break", long_break: "long_break" };

function fmt(s) {
  s = Math.max(0, Math.floor(s));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
}

function fmtMinutes(m) {
  const mins = Math.max(0, Math.floor(m));
  const hrs = Math.floor(mins / 60);
  const rem = mins % 60;
  if (hrs <= 0) return `${rem}m`;
  return `${hrs}h ${rem}m`;
}

function StreakBanner({ summary, loading }) {
  const streak = summary?.current_streak_days ?? 0;
  const today = summary?.today_cycles ?? 0;
  const todayFocus = summary?.today_focus_minutes ?? 0;
  // Render last 7 days worth of dots — primary block if today's streak covers that slot.
  const dots = Array.from({ length: 7 }, (_, i) => {
    const idx = 6 - i; // rightmost is today (idx=0)
    const isToday = idx === 0;
    const active = idx < streak; // streak walks backwards from today
    return { idx, isToday, active };
  });
  return (
    <div
      className="mb-10 border border-border bg-card grid grid-cols-1 md:grid-cols-3 gap-px"
      data-testid="dashboard-streak-banner"
    >
      <div className="bg-card p-5 group">
        <div className="flex items-center justify-between">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
            <span className="text-primary">//</span> focus_streak
          </div>
          <Flame className={`h-4 w-4 ${streak > 0 ? "text-primary" : "text-muted-foreground"}`} />
        </div>
        <div className="mt-3 flex items-baseline gap-2">
          <span
            className="num-mono font-bold text-4xl glitch"
            data-text={`${loading ? "—" : streak}`}
            data-testid="dashboard-streak-value"
          >
            {loading ? "—" : streak}
          </span>
          <span className="font-mono text-xs text-muted-foreground uppercase tracking-widest">
            day{streak === 1 ? "" : "s"}
          </span>
        </div>
        <div className="mt-3 flex items-center gap-1.5" aria-label="7-day streak">
          {dots.map((d) => (
            <span
              key={d.idx}
              className={`streak-dot ${d.active ? "is-active" : ""} ${d.isToday ? "is-today" : ""}`}
              title={d.isToday ? "today" : `-${d.idx}d`}
            />
          ))}
        </div>
      </div>
      <div className="bg-card p-5">
        <div className="flex items-center justify-between">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
            <span className="text-primary">//</span> today_cycles
          </div>
          <Target className="h-4 w-4 text-primary" />
        </div>
        <div className="mt-3 num-mono font-bold text-4xl" data-testid="dashboard-today-cycles">
          {loading ? "—" : today}
        </div>
        <div className="mt-1 font-mono text-xs text-muted-foreground">
          {">"} pomodoros completed today
        </div>
      </div>
      <div className="bg-card p-5">
        <div className="flex items-center justify-between">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
            <span className="text-primary">//</span> today_focus
          </div>
          <Clock className="h-4 w-4 text-primary" />
        </div>
        <div className="mt-3 num-mono font-bold text-4xl" data-testid="dashboard-today-focus">
          {loading ? "—" : fmtMinutes(todayFocus)}
        </div>
        <div className="mt-1 font-mono text-xs text-muted-foreground">
          <Link to="/analytics" className="hover:text-primary transition-colors">
            {">"} view full analytics →
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [timers, setTimers] = useState(null);
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/timers");
      setTimers(data);
    } catch {
      setTimers([]);
    }
  };

  useEffect(() => {
    load();
    api
      .get("/analytics/summary")
      .then(({ data }) => setSummary(data))
      .catch(() => setSummary(null))
      .finally(() => setSummaryLoading(false));
  }, []);

  const onDelete = async (id) => {
    if (!window.confirm("Delete this timer?")) return;
    try {
      await api.delete(`/timers/${id}`);
      toast.success("Timer deleted");
      load();
    } catch {
      toast.error("Could not delete timer");
    }
  };

  return (
    <div className="min-h-screen bg-background bg-grid scanlines">
      <Navbar />
      <main className="mx-auto max-w-7xl px-6 py-12">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div>
            <div className="section-label mb-3">control_room</div>
            <h1 className="font-display font-bold text-4xl sm:text-5xl tracking-tight">My Timers</h1>
            <p className="mt-2 font-mono text-sm text-muted-foreground">{">"} create, share and run focused sessions for your team</p>
          </div>
          <Link to="/new">
            <Button className="rounded-none font-mono uppercase tracking-widest h-11 px-6" data-testid="dashboard-new-timer-btn">
              <Plus className="mr-2 h-4 w-4" /> New Timer
            </Button>
          </Link>
        </div>

        {timers === null && (
          <div className="font-mono text-sm text-muted-foreground"><span className="caret">// loading</span></div>
        )}

        {timers && <StreakBanner summary={summary} loading={summaryLoading} />}

        {timers && timers.length === 0 && (
          <div className="border border-border p-12 text-center" data-testid="dashboard-empty">
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-muted-foreground mb-3">// no timers yet</div>
            <h3 className="font-display font-bold text-2xl">Spin up your first focus session.</h3>
            <p className="mt-2 font-mono text-sm text-muted-foreground">{">"} 25-minute work blocks, shareable in one click</p>
            <Link to="/new" className="inline-block mt-6">
              <Button className="rounded-none font-mono uppercase tracking-widest h-11 px-6" data-testid="dashboard-empty-create-btn">
                <Plus className="mr-2 h-4 w-4" /> Create timer
              </Button>
            </Link>
          </div>
        )}

        {timers && timers.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-border border border-border" data-testid="dashboard-timers-grid">
            {timers.map((t) => (
              <div key={t.id} className="bg-background p-6 flex flex-col gap-4" data-testid={`timer-card-${t.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
                      <span className="text-primary">//</span> {PHASE_LABEL[t.phase]} {t.is_running ? "· running" : "· paused"}
                    </div>
                    <h3 className="font-display font-bold text-xl mt-2 truncate" title={t.name}>{t.name}</h3>
                  </div>
                  <button
                    onClick={() => onDelete(t.id)}
                    className="p-2 border border-border hover:border-destructive hover:text-destructive transition-colors"
                    title="Delete"
                    data-testid={`timer-delete-${t.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>

                <div className="flex items-baseline gap-3">
                  <div className="num-mono font-bold text-4xl" data-testid={`timer-card-time-${t.id}`}>{fmt(t.remaining_seconds)}</div>
                  {t.is_running ? <Play className="h-4 w-4 text-primary" /> : <Pause className="h-4 w-4 text-muted-foreground" />}
                </div>

                <div className="font-mono text-[11px] text-muted-foreground space-y-1">
                  <div>{">"} work {t.work_minutes}m · break {t.short_break_minutes}m · long {t.long_break_minutes}m</div>
                  <div>{">"} cycles_complete: <span className="text-foreground">{t.completed_cycles}</span></div>
                  <div>
                    {">"} sharing: {t.shared ? (
                      <span className="text-primary">{t.share_code}</span>
                    ) : (
                      <span className="text-muted-foreground">disabled</span>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2 mt-auto">
                  <Link to={`/timer/${t.id}`} className="flex-1">
                    <Button className="w-full rounded-none font-mono uppercase tracking-widest" data-testid={`timer-open-${t.id}`}>
                      Open
                    </Button>
                  </Link>
                  {t.shared && (
                    <button
                      className="p-3 border border-border hover:border-primary hover:text-primary transition-colors"
                      title="Copy share link"
                      data-testid={`timer-share-copy-${t.id}`}
                      onClick={() => {
                        const link = `${window.location.origin}/t/${t.share_code}`;
                        navigator.clipboard.writeText(link);
                        toast.success("Share link copied");
                      }}
                    >
                      <Share2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
